/**
 * Google/Microsoft SSO buttons for the login screen. Additive, not a
 * replacement: they sit below the existing email+password form, separated
 * by a divider, because Avrio's whole installed base signs in with a
 * password today -- demoting that path for a brand-new option would break
 * real users' habits, unlike Nebius (no installed base to protect).
 *
 * WHAT IS AND ISN'T VERIFIED SPEC, stated explicitly because getting a
 * brand's own button "helpfully" wrong is a common failure mode:
 *
 * Google -- background #FFFFFF, border #747775 1px, text #1F1F1F, and the
 * "Sign in with Google" wording are published exactly as given in Google's
 * Sign In With Google branding guidelines
 * (developers.google.com/identity/branding-guidelines). Button HEIGHT and
 * CORNER RADIUS are not published as numbers anywhere on that page or the
 * JS reference -- Google's own text there points integrators at its
 * `renderButton()` output as the source of truth instead of a spec to
 * reimplement. 40px / 4px below are Material Design's own standard button
 * metrics, not a cited Google number; flagged rather than presented as
 * verified. The "G" mark's four colors are its own long-standing, stable
 * brand colors, reproduced unaltered per Google's explicit "don't change
 * the size or color of the G" rule.
 *
 * Microsoft's branding guidelines
 * (learn.microsoft.com/entra/identity-platform/howto-add-branding-in-apps)
 * publish almost no CSS spec at all: only the wording ("Sign in with
 * Microsoft", or "Sign in" if space-constrained) and the requirement to use
 * the unaltered four-color logo are actual requirements. Microsoft ships
 * finished PNG/SVG button IMAGES rather than colors/height/radius to
 * rebuild -- there is no official number for this button's chrome to match
 * against. Styled here to match Google's chrome for visual consistency
 * between the two, which is a design decision on this app's part, not a
 * Microsoft compliance requirement.
 */

import { OAuthProvider } from '../../../../fsm/auth';
import styles from './index.module.css';

interface Props {
  disabled?: boolean;
  onSelect: (provider: OAuthProvider) => void;
  /** The login page sits these below an existing password form, so the "or"
   * divider is load-bearing there. The HM activation page has no password
   * field above these -- they're the primary and only path shown at first
   * -- so a divider dividing nothing would be confusing chrome. Defaults to
   * true; every existing call site is unaffected. */
  showDivider?: boolean;
}

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.348 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
    />
  </svg>
);

/** Unaltered four-color Microsoft logo mark, per Microsoft's own guidance. */
const MicrosoftMark = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const SsoButtons = ({ disabled, onSelect, showDivider = true }: Props) => (
  <div className={styles.wrapper}>
    {showDivider && (
      <div className={styles.divider}>
        <span className={styles.dividerLine} aria-hidden="true" />
        <span className={styles.dividerText}>or</span>
        <span className={styles.dividerLine} aria-hidden="true" />
      </div>
    )}

    <button
      type="button"
      className={styles.googleButton}
      disabled={disabled}
      onClick={() => onSelect('google')}
    >
      <GoogleMark />
      Sign in with Google
    </button>

    <button
      type="button"
      className={styles.microsoftButton}
      disabled={disabled}
      onClick={() => onSelect('microsoft')}
    >
      <MicrosoftMark />
      Sign in with Microsoft
    </button>
  </div>
);

export default SsoButtons;
